import { useState } from 'react';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getData, post } from 'services/api';
import { handleDownload } from 'utils/handleLink';
import { QueryResultProps } from 'features/PunchupPages/Orders/types';
import { ALL_TIME_QUERY } from '../constants';
import { IdParamsType } from 'utils/types';
import { postToast } from 'features/common/utils/postFlash';
import { queryClient } from 'index';

export const useCompedTicketsPage = () => {
	const { t } = useTranslation();
	const resultsPerPage = 30;
	const { id } = useParams<IdParamsType>();
	const [searchText, setSearchText] = useState('');

	const initialValueCancelModal = { show: false, id: 0 };
	const [showCancelModal, setShowCancelModal] = useState(initialValueCancelModal);
	const openCancelModal = (id: number) => setShowCancelModal({ show: true, id });
	const closeCancelModal = () => setShowCancelModal(initialValueCancelModal);

	// One param builder for both the paged query and the CSV export so `search` is
	// always URL-encoded and the two requests can never drift apart.
	const buildCompedParams = (extra: Record<string, string>) => {
		const p = new URLSearchParams();
		// Period filtering was removed from the green Comped Tickets controls (TIC-301 parity);
		// the list is always all-time (search is the only active filter).
		p.set('date', ALL_TIME_QUERY);
		p.set('search', searchText);
		p.set('comped', 'true');
		p.set('event_ids', id);
		Object.entries(extra).forEach(([k, v]) => p.set(k, v));
		return p;
	};

	const compedTicketsQuery = useInfiniteQuery({
		queryKey: ['orders', 'comped', searchText, id],
		queryFn: ({ pageParam = 1 }) =>
			getData('/orders?' + buildCompedParams({ page: String(pageParam), per_page: String(resultsPerPage) }).toString()),
		getNextPageParam: (lastPage, pages) => (lastPage.data.length >= resultsPerPage ? pages.length + 1 : undefined),
	});
	const compedTicketsQueryObject = compedTicketsQuery as QueryResultProps;

	const downloadCompedQuery = useMutation(async () => {
		try {
			const res = await getData('/orders/csv?' + buildCompedParams({ per_page: '-1' }).toString());
			handleDownload(res, 'comped-tickets.csv');
		} catch {
			postToast('error', t('compedTickets-csv-error', 'Error downloading CSV'));
		}
	});

	const refetch = () => queryClient.refetchQueries(['orders', 'comped']);

	const { mutate: onCancelInvite } = useMutation(() => post(`/tickets/cancel-comp/${showCancelModal.id}`), {
		onSuccess: () => {
			closeCancelModal();
			queryClient.refetchQueries(['orders']);
			postToast('success', t('compedTickets-cancel-success', 'Invite cancelled'));
		},
		onError: () => {
			postToast('error', t('compedTickets-cancel-error', 'Error cancelling invite'));
			closeCancelModal();
		},
	});
	return {
		setSearchText,
		searchText,
		compedTicketsQueryObject,
		isError: compedTicketsQuery.isError,
		showCancelModal,
		openCancelModal,
		closeCancelModal,
		onCancelInvite,
		downloadCompedQuery,
		refetch,
	};
};
